import * as React from 'react';

import {
  enumBookingStatus,
  BookingStatus,
} from '@rematter/frontend-lib-api-client';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { BookingContainerInput } from '../../_graphql/mutationBookingUpdate';
import * as state from '../../_state/bookingForm.state';

import { BookingWizard } from './_BookingWizard';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { BookingRecord } from '~/modules/trade/types/booking';
import { $facilityId } from '~/shared/providers/auth/identity.localState';
import { maybeParseCalendarDate } from '~/shared/utils/dateTime';

interface BookingDialogProps {
  booking: BookingRecord | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (bookingId?: string) => void;
}

export const BookingDialog = ({
  booking,
  isOpen,
  onClose,
  onSuccess,
}: BookingDialogProps) => {
  const t = useTranslations();
  const toast = useToast();
  const createBooking = useSetAtom(state.$createBooking);
  const updateBooking = useSetAtom(state.$updateBooking);
  const createDraftBooking = useSetAtom(state.$createDraftBooking);
  const updateDraftBooking = useSetAtom(state.$updateDraftBooking);

  const currentFacilityId = useAtomValue($facilityId);
  const formData = useAtomValue(state.$bookingForm);
  const resetForm = useSetAtom(state.$resetBookingForm);
  const updateForm = useSetAtom(state.$updateBookingForm);

  React.useEffect(() => {
    if (isOpen && booking) {
      // Directly map BookingRecord to form state without transformation
      updateForm({
        id: booking.id,
        facilityId: booking.facilityId,
        bookingNumber: booking.bookingNumber,
        status: booking.status,
        cutOffDate: booking.cutOffDate,
        earlyReturnDate: booking.earlyReturnDate,
        vesselArrivalDate: booking.vesselArrivalDate,
        vesselDepartureDate: booking.vesselDepartureDate,
        vesselName: booking.vesselName,
        loadingPortId: booking.loadingPortId,
        loadingPort: booking.loadingPort,
        pickupTerminalId: booking.pickupTerminalId,
        pickupTerminal: booking.pickupTerminal,
        destinationPortId: booking.destinationPortId,
        destinationPort: booking.destinationPort,
        dropoffTerminalId: booking.dropoffTerminalId,
        dropoffTerminal: booking.dropoffTerminal,
        steamshipLine: booking.steamshipLine,
        oceanFreight: booking.oceanFreight,
        internalNotes: booking.internalNotes,
        externalNotes: booking.externalNotes,
        containers: booking.containers,
        salesOrders: booking.salesOrders,
        salesOrderIds: booking.salesOrders?.map((salesOrder) => salesOrder.id),
      });
    } else if (isOpen && !booking) {
      resetForm();
      if (currentFacilityId) {
        updateForm({ facilityId: currentFacilityId });
      }
    }
  }, [isOpen, booking, updateForm, resetForm, currentFacilityId]);

  const handleClose = React.useCallback(() => {
    onClose();
    resetForm();
  }, [onClose, resetForm]);

  const formatContainersData = React.useCallback(
    (
      containers: Array<{
        containerType?: { id: string };
        quantity: number;
      }>,
    ): BookingContainerInput[] =>
      containers
        ?.filter(
          (container) => container.containerType?.id && container.quantity > 0,
        )
        .map((container) => ({
          containerTypeId: container.containerType?.id || '',
          quantity: container.quantity || 0,
        })) ?? [],
    [],
  );

  const prepareBookingData = React.useCallback(
    (isDraft: boolean) => {
      const baseData = {
        facilityId: formData.facilityId || '',
        documentNumber: formData.bookingNumber || '',
        containers: formData.containers
          ? formatContainersData(formData.containers)
          : [],
        cutOffDate: formData.cutOffDate?.toString(),
        earlyReturnDate: formData.earlyReturnDate?.toString(),
        vesselArrivalDate: formData.vesselArrivalDate?.toString(),
        vesselDepartureDate: formData.vesselDepartureDate?.toString(),
        vesselName: formData.vesselName,
        loadingPortId: formData.loadingPortId,
        pickupTerminalId: formData.pickupTerminalId,
        destinationPortId: formData.destinationPortId,
        dropoffTerminalId: formData.dropoffTerminalId,
        steamshipLine: formData.steamshipLine,
        oceanFreight: formData.oceanFreight,
        internalNotes: formData.internalNotes,
        externalNotes: formData.externalNotes,
        salesOrderIds: formData.salesOrders?.map((salesOrder) => salesOrder.id),
        ...(isDraft ? {} : { status: enumBookingStatus.Open }),
      };

      return baseData;
    },
    [formData, formatContainersData],
  );

  const handleSaveAsDraft = React.useCallback(async () => {
    const bookingData = prepareBookingData(true);

    if (booking || formData.id) {
      // Update existing draft
      const result = await updateDraftBooking({
        id: booking?.id || formData.id || '',
        ...bookingData,
      });
      // Update form state with the returned data
      if (result.tradeBookingUpdateDraft) {
        const updatedBooking = result.tradeBookingUpdateDraft;
        updateForm({
          id: updatedBooking.id,
          facilityId: updatedBooking.facilityId,
          bookingNumber: updatedBooking.documentNumber,
          status: updatedBooking.status as BookingStatus,
          cutOffDate: maybeParseCalendarDate(updatedBooking.cutOffDate),
          earlyReturnDate: maybeParseCalendarDate(
            updatedBooking.earlyReturnDate,
          ),
          vesselArrivalDate: maybeParseCalendarDate(
            updatedBooking.vesselArrivalDate,
          ),
          vesselDepartureDate: maybeParseCalendarDate(
            updatedBooking.vesselDepartureDate,
          ),
          vesselName: updatedBooking.vesselName ?? undefined,
          loadingPortId: updatedBooking.loadingPortId ?? undefined,
          loadingPort: updatedBooking.loadingPort ?? undefined,
          pickupTerminalId: updatedBooking.pickupTerminalId ?? undefined,
          pickupTerminal: updatedBooking.pickupTerminal ?? undefined,
          destinationPortId: updatedBooking.destinationPortId ?? undefined,
          destinationPort: updatedBooking.destinationPort ?? undefined,
          dropoffTerminalId: updatedBooking.dropoffTerminalId ?? undefined,
          dropoffTerminal: updatedBooking.dropoffTerminal ?? undefined,
          steamshipLine: updatedBooking.steamshipLine ?? undefined,
          oceanFreight: updatedBooking.oceanFreight ?? undefined,
          internalNotes: updatedBooking.internalNotes ?? undefined,
          externalNotes: updatedBooking.externalNotes ?? undefined,
          salesOrders: updatedBooking.salesOrders ?? undefined,
          salesOrderIds:
            updatedBooking.salesOrders && updatedBooking.salesOrders.length > 0
              ? updatedBooking.salesOrders?.map((salesOrder) => salesOrder.id)
              : undefined,
          containers:
            updatedBooking.containers?.map((container) => ({
              containerType: {
                id: container.containerType.id,
                name: container.containerType.name,
                timestamps: {
                  createdAt: '',
                  updatedAt: '',
                },
              },
              quantity: container.quantity,
            })) ?? [],
        });
      }
    } else {
      // Create new draft
      const result = await createDraftBooking(bookingData);
      // Update form state with the new booking data
      if (result.tradeBookingCreateDraft) {
        const createdBooking = result.tradeBookingCreateDraft;
        updateForm({
          id: createdBooking.id,
          facilityId: createdBooking.facilityId,
          bookingNumber: createdBooking.documentNumber,
          status: createdBooking.status as BookingStatus,
          cutOffDate: maybeParseCalendarDate(createdBooking.cutOffDate),
          earlyReturnDate: maybeParseCalendarDate(
            createdBooking.earlyReturnDate,
          ),
          vesselArrivalDate: maybeParseCalendarDate(
            createdBooking.vesselArrivalDate,
          ),
          vesselDepartureDate: maybeParseCalendarDate(
            createdBooking.vesselDepartureDate,
          ),
          vesselName: createdBooking.vesselName ?? undefined,
          loadingPortId: createdBooking.loadingPortId ?? undefined,
          loadingPort: createdBooking.loadingPort ?? undefined,
          pickupTerminalId: createdBooking.pickupTerminalId ?? undefined,
          pickupTerminal: createdBooking.pickupTerminal ?? undefined,
          destinationPortId: createdBooking.destinationPortId ?? undefined,
          destinationPort: createdBooking.destinationPort ?? undefined,
          dropoffTerminalId: createdBooking.dropoffTerminalId ?? undefined,
          dropoffTerminal: createdBooking.dropoffTerminal ?? undefined,
          steamshipLine: createdBooking.steamshipLine ?? undefined,
          oceanFreight: createdBooking.oceanFreight ?? undefined,
          internalNotes: createdBooking.internalNotes ?? undefined,
          externalNotes: createdBooking.externalNotes ?? undefined,
          containers:
            createdBooking.containers?.map((container) => ({
              containerType: {
                id: container.containerType.id,
                name: container.containerType.name,
                timestamps: {
                  createdAt: '',
                  updatedAt: '',
                },
              },
              quantity: container.quantity,
            })) ?? [],
        });
      }
    }

    // Don't call onSuccess here - let the wizard handle when to close and refresh
  }, [
    booking,
    formData.id,
    prepareBookingData,
    updateDraftBooking,
    createDraftBooking,
    updateForm,
  ]);

  const handleSave = React.useCallback(async () => {
    const bookingData = prepareBookingData(false);

    if (booking || formData.id) {
      // Update existing booking (either passed as prop or created as draft)
      const id = booking?.id || formData.id || '';
      const updatedBooking = await updateBooking({
        id,
        ...bookingData,
      });

      toast.success(t.bookingsView.messages.updateSuccess);

      return updatedBooking.tradeBookingUpdate.booking?.id || id;
    } else {
      // Create new booking with Open status
      const newBooking = await createBooking({
        ...bookingData,
        status: bookingData.status as keyof typeof enumBookingStatus,
      });

      toast.success(t.bookingsView.messages.createSuccess);

      return newBooking.tradeBookingCreate.id;
    }

    // Don't call onSuccess here - let the wizard handle it
  }, [
    booking,
    formData.id,
    prepareBookingData,
    updateBooking,
    createBooking,
    t,
    toast,
  ]);

  return (
    <BookingWizard
      bookingId={booking?.id}
      isOpen={isOpen}
      onClose={handleClose}
      onSaveAsDraft={handleSaveAsDraft}
      onSave={handleSave}
      onSuccess={onSuccess}
      className="@BookingDialog"
    />
  );
};
